// @em/utils/browserTelemetry
//
// ROADMAP #189 — Azure Application Insights in the browser: uncaught errors, failed and slow API calls, page load times, tied
// to the server's trace of the same call. Shared by the app and the public event site.
// ⓘ Nothing is loaded without a connection string. It is public by design: the browser has to send with it.
// ⚠ IT OBEYS THE ANALYTICS CONSENT (`analyticsConsent.ts`): nothing on a browser that declined or sends Global Privacy Control, or
//   in a region we ask until it says yes. No cookies and no browser storage: the SDK's user and session ids live only as long as the
//   page, so nobody can be followed from one visit to the next.
// ⚠ NOT IMPORTED BY THE BARREL: it must not reach a server bundle.
// ⚠ A CORS-correlated call carries `traceparent` / `tracestate`, which the gateway must allow first (its `allowedHeaders`), or EVERY
//   call fails its preflight. So: deploy the gateway, then set the connection string.
import type { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';

import { browserTimeZone, effectiveConsent, privacySignalOn, readStoredConsent, subscribeConsent } from './analyticsConsent';
import { safePageLocation } from './analyticsPrivacy';

export interface BrowserTelemetryOptions {
  connectionString: string | null | undefined;
  /** How this app shows up in the reports ("eventsmagic-app"). */
  role: string;
  /** The hosts whose calls are worth reporting; every other request is dropped (Google, Stripe, images). */
  apiHosts: string[];
  /** Tie calls to these hosts to the server's trace. ⚠ Their CORS must allow the headers: see the file header. */
  correlate?: boolean;
  /** Share of sessions sent, 1–100. */
  samplingPercentage?: number;
}

export interface BrowserTelemetry {
  trackEvent(name: string, properties?: Record<string, string | number | boolean>): void;
  trackPageView(name: string, uri: string): void;
}

const withoutEmails = (value: string): string => value.replace(/[^\s"'`<>(),;:=]+@[^\s"'`<>(),;:=]+\.[a-z]{2,}/gi, '[email]');

/** The host of a call's target, which the SDK gives as a full url, or as "host:port". ⚠ `new URL` reads "host:443" as a scheme. */
const hostOf = (value: unknown): string =>
  String(value ?? '')
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .split(/[/:?#]/)[0]
    .toLowerCase();

/** Scrubs one item in place; false drops it. ⚠ Pure, so `check-analytics` runs it. */
export const scrubTelemetryItem = (item: ITelemetryItem, role: string, apiHosts: string[]): boolean => {
  const data = (item.baseData ?? {}) as Record<string, any>;

  if (item.baseType === 'RemoteDependencyData' && !apiHosts.includes(hostOf(data.target))) {
    return false;
  }

  for (const key of ['uri', 'refUri', 'url', 'target', 'data']) {
    if (typeof data[key] === 'string') {
      data[key] = safePageLocation(data[key]);
    }
  }

  if (typeof data.name === 'string') {
    data.name = withoutEmails(data.name.replace(/[?#][\s\S]*$/, ''));
  }

  for (const exception of Array.isArray(data.exceptions) ? data.exceptions : []) {
    for (const key of ['message', 'stack']) {
      if (typeof exception[key] === 'string') {
        exception[key] = withoutEmails(exception[key]);
      }
    }

    for (const frame of Array.isArray(exception.parsedStack) ? exception.parsedStack : []) {
      if (typeof frame?.assembly === 'string') {
        frame.assembly = withoutEmails(frame.assembly);
      }
    }
  }

  // ⚠ An error repeats its message and its url in the properties.
  for (const holder of [data.properties, item.data]) {
    for (const [key, value] of Object.entries((holder ?? {}) as Record<string, unknown>)) {
      if (typeof value === 'string') {
        (holder as Record<string, unknown>)[key] = key === 'url' ? safePageLocation(value) : withoutEmails(value);
      }
    }
  }

  item.tags = { ...(item.tags ?? {}), 'ai.cloud.role': role };

  return true;
};

/**
 * The GraphQL operation a call is for, from its body, or null. ⚠ Only the name: the rest of the body holds what people typed.
 * ⚠ Pure, so `check-analytics` runs it. A name is letters, digits and `_`, so nothing else can reach the report.
 */
export const graphqlOperationOf = (body: unknown): string | null => {
  if (typeof body !== 'string' || !body.startsWith('{')) {
    return null;
  }

  try {
    const parsed = JSON.parse(body) as { operationName?: unknown; query?: unknown };
    const named = typeof parsed.operationName === 'string' ? parsed.operationName : null;
    const written = typeof parsed.query === 'string' ? /^\s*(?:query|mutation|subscription)\s+([A-Za-z_][A-Za-z0-9_]*)/.exec(parsed.query)?.[1] : null;
    const name = named ?? written ?? null;

    return name && /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) ? name : null;
  } catch {
    return null;
  }
};

const allowed = (): boolean => effectiveConsent(readStoredConsent(), browserTimeZone(), privacySignalOn()) === 'granted';

/** Starts collecting, now or as soon as the visitor allows it. Safe to call anywhere in the browser; does nothing on the server. */
export const startBrowserTelemetry = (options: BrowserTelemetryOptions): BrowserTelemetry => {
  const connectionString = options.connectionString?.trim();
  let sdk: ApplicationInsights | null = null;
  let loading = false;
  // Only what happened while the SDK was on its way: nothing is kept for a visitor who has not allowed it.
  let waiting: Array<(ready: ApplicationInsights) => void> = [];

  const load = async (): Promise<void> => {
    if (sdk || loading) {
      return;
    }

    loading = true;

    try {
      const { ApplicationInsights: Sdk, DistributedTracingModes } = await import('@microsoft/applicationinsights-web');
      const instance = new Sdk({
        config: {
          connectionString,
          disableCookiesUsage: true,
          isStorageUseDisabled: true,
          enableSessionStorageBuffer: false,
          samplingPercentage: options.samplingPercentage ?? 100,
          enableUnhandledPromiseRejectionTracking: true,
          enableAutoRouteTracking: false,
          autoTrackPageVisitTime: false,
          distributedTracingMode: DistributedTracingModes.W3C,
          enableCorsCorrelation: !!options.correlate,
          correlationHeaderDomains: options.apiHosts,
        },
      });

      instance.loadAppInsights();
      instance.addTelemetryInitializer((item) => scrubTelemetryItem(item, options.role, options.apiHosts));
      // Every API call is the same `POST …/graphql`: name it as the server names its side, so the two can be read together.
      instance.addDependencyListener((details) => {
        const operation = graphqlOperationOf(details.init?.body);

        // ⚠ Assigned whole: `details.context` starts out empty, and a value set on nothing never reached the initializer below.
        if (operation) {
          details.context = { ...details.context, graphqlOperation: operation };
        }
      });
      instance.addDependencyInitializer((details) => {
        const operation = details.context?.graphqlOperation as string | undefined;

        if (operation) {
          details.item.name = `POST /graphql/${operation}`;
          details.item.properties = { ...details.item.properties, graphqlOperation: operation };
        }
      });
      sdk = instance;
      waiting.forEach((run) => run(instance));
    } catch {
      // Telemetry must never be what breaks a page.
    } finally {
      waiting = [];
      loading = false;
    }
  };

  const whenReady = (run: (ready: ApplicationInsights) => void): void => {
    if (sdk) {
      if (!sdk.config.disableTelemetry) {
        run(sdk);
      }
    } else if (loading) {
      waiting.push(run);
    }
  };

  if (connectionString && typeof window !== 'undefined') {
    if (allowed()) {
      void load();
    }

    subscribeConsent((consent) => {
      if (consent === 'granted') {
        if (sdk) {
          sdk.config.disableTelemetry = false;
        } else {
          void load();
        }
      } else if (sdk) {
        sdk.config.disableTelemetry = true;
      }
    });
  }

  return {
    trackEvent: (name, properties) => whenReady((ready) => ready.trackEvent({ name }, properties)),
    trackPageView: (name, uri) => whenReady((ready) => ready.trackPageView({ name, uri: safePageLocation(uri) })),
  };
};
